import { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, MapPin, X } from "lucide-react";
import { Reveal, SectionLabel, SectionTitle } from "@/components/Reveal";
import { lavori } from "@/data/lavori";

export default function LavoroDetail() {
  const { slug } = useParams();
  const lavoro = lavori.find((l) => l.slug === slug);
  const [lightbox, setLightbox] = useState(null);

  if (!lavoro) return <Navigate to="/lavori" replace />;

  return (
    <main data-testid={`lavoro-${slug}`} className="bg-slate-50">
      {/* HEADER */}
      <section className="pt-36 pb-16 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/lavori"
            data-testid="lavoro-back-link"
            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft size={14} /> Tutti i lavori
          </Link>
          <Reveal>
            <SectionLabel className="mt-6">{lavoro.category}</SectionLabel>
            <h1 className="font-serif font-medium mt-5 text-4xl md:text-6xl tracking-tight leading-[1.05] text-slate-900">
              {lavoro.title}
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-3xl text-lg text-slate-600 leading-relaxed">
              {lavoro.short}
            </p>
          </Reveal>
        </div>
      </section>

      {/* PROJECTS */}
      {lavoro.projects.map((p, idx) => (
        <section
          key={p.name}
          data-testid={`lavoro-project-${idx}`}
          className={`py-20 lg:py-24 ${idx % 2 === 0 ? "bg-slate-50" : "bg-white"}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-12 gap-10 mb-12">
              <div className="lg:col-span-5">
                <Reveal>
                  <div className="text-[10px] tracking-[0.22em] uppercase text-amber-800 font-semibold">
                    Progetto {String(idx + 1).padStart(2, "0")}
                  </div>
                  <h2 className="font-serif text-3xl md:text-4xl mt-4 text-slate-900 leading-tight">
                    {p.name}
                  </h2>
                  {p.location && (
                    <div className="mt-4 flex items-center gap-2 text-sm text-slate-500">
                      <MapPin size={14} className="text-amber-800" /> {p.location}
                    </div>
                  )}
                </Reveal>
              </div>
              <div className="lg:col-span-7 lg:pl-10 lg:border-l lg:border-slate-200">
                <Reveal delay={0.1}>
                  <p className="text-base text-slate-700 leading-relaxed">
                    {p.description}
                  </p>
                </Reveal>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
              {p.images.map((img, i) => (
                <Reveal key={i} delay={i * 0.04}>
                  <button
                    type="button"
                    onClick={() => setLightbox({ src: img.src, caption: img.caption, name: p.name })}
                    data-testid={`lavoro-image-${idx}-${i}`}
                    className="group block w-full text-left bg-white border border-slate-200 hover:border-slate-900 transition-colors"
                  >
                    <div className="aspect-[4/3] img-zoom bg-slate-100 overflow-hidden">
                      <img
                        src={img.src}
                        alt={`${p.name} — ${img.caption}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <div className="text-[10px] tracking-[0.18em] uppercase text-amber-800 font-semibold">
                        {p.name}
                      </div>
                      <div className="text-sm text-slate-700 mt-1">{img.caption}</div>
                    </div>
                  </button>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-5xl leading-tight">
              Lavoriamo insieme.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/contatti"
              data-testid="lavoro-cta-contact"
              className="mt-8 inline-flex items-center gap-2 bg-amber-300 text-slate-900 px-8 py-4 rounded-full font-medium hover:bg-amber-200 transition-colors"
            >
              Richiedi un preventivo <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* LIGHTBOX */}
      {lightbox && (
        <div
          data-testid="lavoro-lightbox"
          className="fixed inset-0 z-[60] bg-slate-950/95 flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
        >
          <button
            data-testid="lavoro-lightbox-close"
            onClick={() => setLightbox(null)}
            className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
            aria-label="Chiudi"
          >
            <X size={20} />
          </button>
          <div className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightbox.src}
              alt={lightbox.name}
              className="w-full max-h-[80vh] object-contain"
            />
            <div className="text-center text-white mt-4">
              <div className="font-serif text-xl">{lightbox.name}</div>
              <div className="text-sm text-slate-300 mt-1">{lightbox.caption}</div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
